import React from "react";

import {
  Alert,
  Button,
  ButtonGroup,
  IconButton,
  InputAdornment,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import AppsIcon from "@mui/icons-material/Apps";
import TableRowsIcon from "@mui/icons-material/TableRows";

import { Patient, PatientSummary } from "../../types";

import { useAddPatient, useListPatients } from "../../store";

import MainLayout from "../../layouts/main-layout";
import PatientsTableView from "./components/patients-table-view";
import PatientsCardsView from "./components/patients-cards-view";
import AddPatientDialog from "./components/add-patient-dialog";

const getPatientMeetingDates = (patient: PatientSummary) => {
  const dates: Date[] = [];

  if (patient.lastMeeting?.startTime) {
    dates.push(patient.lastMeeting.startTime);
  }

  if (patient.nextMeeting?.startTime) {
    dates.push(patient.nextMeeting.startTime);
  }

  return dates;
};

/**
 * Patients page component that displays a list of patients in either card or table view, with search and sorting functionality.
 */
const PatientsPage: React.FC = () => {
  const { data: patients, isLoading } = useListPatients();

  const onSuccessfulAdd = (patient: Patient | null) => {
    console.log("Patient added successfully", patient);
    setSnackbar({
      open: true,
      message: patient
        ? `Patient ${patient.displayId} created successfully`
        : "Patient created successfully",
      severity: "success",
    });
  };

  const onFailedAdd = (err: unknown) => {
    console.error("Failed to add patient", err);
    setSnackbar({
      open: true,
      message: "Failed to add patient",
      severity: "error",
    });
  };

  const { mutate: addPatient, isPending: isAddingPatient } = useAddPatient(
    onSuccessfulAdd,
    onFailedAdd,
  );

  const [snackbar, setSnackbar] = React.useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const [addDialogOpen, setAddDialogOpen] = React.useState<boolean>(false);

  const [searchQuery, setSearchQuery] = React.useState<string>("");

  const [viewMode, setViewMode] = React.useState<"cards" | "list">("list");

  // Filter and sort patients
  const filteredPatients = React.useMemo(() => {
    if (!patients) {
      return [];
    }

    // Filter based on search query
    const query = searchQuery.toLowerCase().trim();
    const filtered = query
      ? patients.filter(
          (patient) =>
            patient.patientId.toLowerCase().includes(query) ||
            patient.displayId.toLowerCase().includes(query),
        )
      : patients;

    const now = new Date();

    return [...filtered].sort((a, b) => {
      // Helper to get valid meeting dates (not null/undefined)
      const aDates = getPatientMeetingDates(a);
      const bDates = getPatientMeetingDates(b);

      // Find soonest upcoming meeting
      const aUpcoming = aDates
        .filter((d) => d >= now)
        .sort((x, y) => x.getTime() - y.getTime())[0];
      const bUpcoming = bDates
        .filter((d) => d >= now)
        .sort((x, y) => x.getTime() - y.getTime())[0];

      if (aUpcoming && bUpcoming) {
        return aUpcoming.getTime() - bUpcoming.getTime();
      }
      if (aUpcoming && !bUpcoming) return -1;
      if (!aUpcoming && bUpcoming) return 1;

      // If no upcoming, find most recent past meeting
      const aRecent = aDates
        .filter((d) => d < now)
        .sort((x, y) => y.getTime() - x.getTime())[0];
      const bRecent = bDates
        .filter((d) => d < now)
        .sort((x, y) => y.getTime() - x.getTime())[0];

      if (aRecent && bRecent) {
        return bRecent.getTime() - aRecent.getTime();
      }
      if (aRecent && !bRecent) return -1;
      if (!aRecent && bRecent) return 1;

      // Fallback: alphabetical by displayId
      return a.displayId.localeCompare(b.displayId);
    });
  }, [patients, searchQuery]);

  const handleSnackbarClose = (
    _event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleViewChange = (newView: "cards" | "list") => {
    setViewMode(newView);
  };

  const openAddPatient = () => {
    setAddDialogOpen(true);
  };

  const confirmAddPatient = () => {
    addPatient();
    setAddDialogOpen(false);
  };

  const cancelAddPatient = () => {
    setAddDialogOpen(false);
  };

  return (
    <MainLayout fixedHeight>
      <Stack
        spacing={0}
        sx={{
          flex: 1,
          minHeight: 0,
        }}
      >
        <Stack
          direction={"row"}
          justifyContent="space-between"
          alignItems="center"
          sx={{ flexShrink: 0 }}
          mb={2}
        >
          <Typography variant="h3">Patients</Typography>
          <Button
            variant="contained"
            onClick={openAddPatient}
            startIcon={<AddIcon />}
            disabled={isAddingPatient}
            sx={{
              borderRadius: "1.5rem",
              px: 2,
              py: 1,
              height: "40px",
              fontWeight: 600,
            }}
          >
            Add Patient
          </Button>
        </Stack>

        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{ flexShrink: 0 }}
        >
          <ButtonGroup
            variant="outlined"
            sx={{
              borderRadius: "0.5rem",
              "& .MuiButton-root": {
                color: "#555",
                borderColor: "#BBB",
                "&:not(:last-child)": {
                  borderRight: "1px solid #BBB",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#f5f5f5",
                  color: "#999",
                },
              },
              "& .MuiButton-root:first-of-type": {
                borderTopLeftRadius: "0.5rem",
                borderBottomLeftRadius: "0.5rem",
              },
              "& .MuiButton-root:last-of-type": {
                borderTopRightRadius: "0.5rem",
                borderBottomRightRadius: "0.5rem",
              },
            }}
          >
            <Tooltip title="List view">
              <Button
                onClick={() => handleViewChange("list")}
                aria-pressed={viewMode === "list"}
                sx={{ px: 1.5, py: 0.875, minWidth: 0 }}
              >
                <TableRowsIcon
                  sx={{
                    fontSize: 22,
                    color: viewMode === "list" ? "primary.main" : "#555",
                  }}
                />
              </Button>
            </Tooltip>
            <Tooltip title="Card view">
              <Button
                onClick={() => handleViewChange("cards")}
                aria-pressed={viewMode === "cards"}
                sx={{ px: 1.5, py: 0.875, minWidth: 0 }}
              >
                <AppsIcon
                  sx={{
                    fontSize: 22,
                    color: viewMode === "cards" ? "primary.main" : "#555",
                  }}
                />
              </Button>
            </Tooltip>
          </ButtonGroup>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Search patients by ID"
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="Clear patient search"
                    onClick={() => setSearchQuery("")}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "0.5rem",
              },
            }}
          />
        </Stack>

        <Stack
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            overflow: "visible",
          }}
        >
          {isLoading ? (
            // Fixing issue where box created a type union error
            <Stack
              justifyContent={"center"}
              alignItems={"center"}
              height={200}
              display={"flex"}
            >
              <CircularProgress />
            </Stack>
          ) : filteredPatients.length === 0 ? (
            <Stack
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: 200,
                flexDirection: "column",
              }}
            >
              <Typography variant="h6" color="text.secondary">
                No patients found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your search or add a new patient
              </Typography>
            </Stack>
          ) : viewMode === "cards" ? (
            <PatientsCardsView
              filteredPatients={filteredPatients}
              unfilteredPatientsCount={patients?.length || 0}
            />
          ) : (
            <PatientsTableView
              filteredPatients={filteredPatients}
              unfilteredPatientsCount={patients?.length || 0}
            />
          )}
        </Stack>
      </Stack>

      <AddPatientDialog
        open={addDialogOpen}
        canAddPatient={!isAddingPatient}
        onConfirm={confirmAddPatient}
        onCancel={cancelAddPatient}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainLayout>
  );
};

export default PatientsPage;
